import { AssistantAvatar, UserAvatar } from "@components/Avatar";
import { ProposeBatchCard } from "@features/requirements/components/ProposeBatchCard";
import { QuickReplyChips } from "@features/requirements/components/QuickReplyChips";
import {
  RequirementPatchCard,
  patchFromToolCall,
} from "@features/requirements/components/RequirementPatchCard";
import { useChatSurface } from "@layouts/AppLayout/useLayoutMode";
import type { ChatMessage as ChatMessageType } from "@models/chat";
import CheckIcon from "@mui/icons-material/CheckRounded";
import ContentCopyIcon from "@mui/icons-material/ContentCopyRounded";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import { useAuthStore } from "@stores/authStore";
import { parseMarkdownTable } from "@utils/bomParse";
import { extractSvg } from "@utils/svgExtract";
import { useMemo, useState } from "react";
import { useDetectSnapshotInTurn } from "../hooks/useDetectSnapshotInTurn";
import { ArtifactView } from "./ArtifactView";
import { BomExportButton, BomTable } from "./BomTable";
import { IterationCard } from "./IterationCard";
import { MarkdownRenderer } from "./MarkdownRenderer";
import { MermaidDiagram } from "./MermaidDiagram";
import { SchematicSvg } from "./SchematicSvg";
import { ToolCallsPanel } from "./ToolCallsPanel";

function CopyMessageButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked */
    }
  };
  return (
    <Tooltip title={copied ? "Copied" : "Copy message"}>
      <IconButton
        size="small"
        aria-label="Copy message"
        onClick={handleCopy}
        sx={{
          p: "3px",
          color: "text.disabled",
          "&:hover": { color: "text.primary" },
        }}
      >
        {copied ? (
          <CheckIcon sx={{ fontSize: 14, color: "success.main" }} />
        ) : (
          <ContentCopyIcon sx={{ fontSize: 14 }} />
        )}
      </IconButton>
    </Tooltip>
  );
}

interface ChatMessageProps {
  message: ChatMessageType;
  /**
   * When true, snapshot-bearing assistant turns render the IterationCard
   * in compact mode (collapsed preview). The latest assistant turn
   * passes false so it gets the full treatment; everything older in
   * scrollback collapses so a 50-message chat doesn't drown.
   */
  compactIteration?: boolean;
  /** Latest assistant turn? Drives quick-reply chip interactivity —
   *  older turns' chips are dimmed because the conversation has moved
   *  past them. */
  isLatestAssistant?: boolean;
  /** Send a follow-up turn into the same chat — used by quick-reply
   *  chips to dispatch the chosen option. */
  onSendFollowUp?: (text: string) => void;
  isStreaming?: boolean;
}

/** Collapse 3+ consecutive newlines to 2 to avoid excessive blank space */
function normalizeContent(content: string): string {
  return content.replace(/\n{3,}/g, "\n\n").trim();
}

function formatTime(ts: string): string {
  return new Date(ts).toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function ChatMessage({
  message,
  compactIteration = false,
  isLatestAssistant = false,
  onSendFollowUp,
  isStreaming = false,
}: ChatMessageProps) {
  const isUser = message.role === "user";
  const user = useAuthStore((s) => s.user);
  // Surface awareness — the requirements assistant has its own
  // dedicated card vocabulary (propose, update, phase_change,
  // quick_replies). Showing the raw ToolCallsPanel underneath all of
  // that is noise: the cards already carry the meaningful tool
  // output. Hide the panel on this surface; keep it everywhere else.
  const surface = useChatSurface();
  const hideToolCallsPanel = surface === "requirements";
  // Snapshot-producing turn? Hook is cheap (memo over tool_calls); call
  // unconditionally so the result is stable across re-renders.
  const detectedSnapshot = useDetectSnapshotInTurn(message.tool_calls);

  // All hooks below are needed only on the assistant branch, but Rules
  // of Hooks require an unconditional call order. If the same component
  // instance ever flips role (streaming placeholder, list reorder, key
  // reuse) the previous user-branch render would have called fewer
  // hooks than this assistant-branch render → React error #310. Keep
  // them above the early `return` and let the empty-tool_calls path
  // short-circuit them cheaply for user messages.
  const allPatches = useMemo(() => {
    if (!message.tool_calls?.length) return [];
    const out: ReturnType<typeof patchFromToolCall>[] = [];
    for (const call of message.tool_calls) {
      const patch = patchFromToolCall(call.name, call.result);
      if (patch) out.push(patch);
    }
    return out.filter((p): p is NonNullable<typeof p> => p !== null);
  }, [message.tool_calls]);

  const quickReplyOptions = useMemo(() => {
    const opts: string[] = [];
    for (const p of allPatches) {
      if (p.kind === "quick_replies") opts.push(...p.options);
    }
    return opts;
  }, [allPatches]);

  const requirementPatches = useMemo(
    () => allPatches.filter((p) => p.kind !== "quick_replies"),
    [allPatches],
  );

  const proposeBatch = useMemo(
    () =>
      requirementPatches
        .filter(
          (p): p is Extract<typeof p, { kind: "propose" }> =>
            p.kind === "propose",
        )
        .map((p) => ({
          category: p.preview.category,
          statement: p.preview.statement,
          requirementId: p.requirementId,
        })),
    [requirementPatches],
  );

  const otherPatches = useMemo(
    () => requirementPatches.filter((p) => p.kind !== "propose"),
    [requirementPatches],
  );

  if (isUser) {
    // Phase 3b — attribute user turns in org chats. When the backend
    // hydrates historical messages it attaches author_user_id / name /
    // picture from the :Event → :User join. Live-stream messages (turns
    // typed right now) don't carry that data — fall back to the signed-
    // in user, which is always the live author.
    const isLiveOrSelf =
      !message.author_user_id || message.author_user_id === user?.user_id;
    const authorName = isLiveOrSelf
      ? user?.name
      : (message.author_name ?? "Member");
    const authorPicture = isLiveOrSelf
      ? user?.picture
      : (message.author_picture ?? null);

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "row-reverse",
          alignItems: "flex-start",
          gap: 1.25,
          px: { xs: 1.5, sm: 3 },
          py: 1,
          "&:hover .message-actions": { opacity: 1 },
        }}
      >
        <UserAvatar
          name={authorName}
          picture={authorPicture}
          size={30}
          sx={{ mt: 0.25 }}
        />
        <Box sx={{ maxWidth: { xs: "85%", sm: "72%" }, minWidth: 0 }}>
          {/* Author label for non-self turns so org members know who wrote
              which question. Skipped for self / personal chats to keep
              them visually clean. */}
          {!isLiveOrSelf && (
            <Typography
              variant="caption"
              sx={{
                display: "block",
                textAlign: "right",
                color: "text.secondary",
                fontSize: "0.7rem",
                fontWeight: 600,
                mb: 0.25,
              }}
            >
              {authorName}
            </Typography>
          )}
          <Box
            sx={{
              bgcolor: "accent.soft",
              border: "1px solid",
              borderColor: "border.accent",
              borderRadius: 2.5,
              px: 2,
              py: 1.25,
              "& p": {
                m: 0,
                color: "text.primary",
                fontSize: "0.9375rem",
                lineHeight: 1.55,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              },
            }}
          >
            <Typography component="p">{message.content}</Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 0.5,
              mt: 0.5,
            }}
          >
            <Box
              className="message-actions"
              sx={{
                opacity: 0,
                transition: (t) => `opacity ${t.motion.fast}`,
              }}
            >
              <CopyMessageButton text={message.content} />
            </Box>
            {message.timestamp && (
              <Typography
                variant="caption"
                sx={{
                  color: "text.disabled",
                  fontSize: "0.68rem",
                }}
              >
                {formatTime(message.timestamp)}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    );
  }

  // Assistant — full-width layout, no bubble
  const { text: textWithoutSvg, svgs } = extractSvg(message.content);
  // BOM messages need bespoke layout — the markdown <table> render path
  // nests an inner scroll container inside the chat scroll (double-scroll
  // trap) and loses column-width sync. Split the content into prose-around
  // + structured rows and hand the rows to <BomTable>. The dedicated
  // BomTable also renders its own export button, so when the parse
  // succeeds we suppress the standalone <BomExportButton> further down.
  const bomTable =
    message.is_bom && textWithoutSvg
      ? parseMarkdownTable(textWithoutSvg)
      : null;

  return (
    <Box
      sx={{
        display: "flex",
        gap: 1.75,
        px: { xs: 1.5, sm: 3 },
        py: 1.25,
        alignItems: "flex-start",
        "&:hover .message-actions": { opacity: 1 },
      }}
    >
      <AssistantAvatar size={30} sx={{ mt: 0.25 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            display: "block",
            mb: 0.5,
            fontFamily: (t) => t.fonts.mono,
            fontSize: "0.65rem",
            fontWeight: 500,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: "text.secondary",
          }}
        >
          Circuit Forge
        </Typography>

        {bomTable ? (
          <>
            {bomTable.before && (
              <MarkdownRenderer>
                {normalizeContent(bomTable.before)}
              </MarkdownRenderer>
            )}
            <BomTable table={bomTable} rawContent={textWithoutSvg} />
            {bomTable.after && (
              <MarkdownRenderer>
                {normalizeContent(bomTable.after)}
              </MarkdownRenderer>
            )}
          </>
        ) : (
          textWithoutSvg && (
            <MarkdownRenderer>
              {normalizeContent(textWithoutSvg)}
            </MarkdownRenderer>
          )
        )}

        {Array.isArray(message.mermaid) &&
          message.mermaid.map((code) => (
            <MermaidDiagram key={code} code={code} />
          ))}

        {svgs.map((svg) => (
          // SVG content itself is the key — stable across re-renders
          // even if `svgs` is reordered, which the array-index form
          // wasn't (parent message is keyed, but biome's rule still
          // wants a content-derived key inside the inner map).
          <SchematicSvg key={svg} svg={svg} />
        ))}

        {/* Inline visual artifacts — images (PNG/SVG/etc.) and
            interactive chart specs. The agent's render_finding /
            render_circuit / simulate_* tools persist their output
            server-side and return a tiny artifact reference (URL
            only, never the bytes). Surfacing the visual here, beneath
            the assistant's prose, is what gives the user the chart
            they asked for. Non-visual artifacts (netlists, raw JSON,
            CSVs) stay confined to the side panel — "show your work"
            detail, not the actual answer. */}
        {message.tool_calls?.flatMap((call, ci) =>
          (call.artifacts ?? [])
            .filter(
              (a) =>
                a.kind === "image" ||
                a.kind === "chart" ||
                (a.content_type ?? "").startsWith("image/") ||
                (a.content_type ?? "") === "application/vnd.cf.chart+json",
            )
            .map((a, ai) => (
              <ArtifactView
                key={`inline-art-${ci}-${ai}-${a.url}`}
                artifact={a}
                variant="inline"
              />
            )),
        )}

        {(otherPatches.length > 0 || proposeBatch.length > 0) && (
          <Box sx={{ mt: 0.5 }}>
            {proposeBatch.length > 0 && (
              <ProposeBatchCard proposals={proposeBatch} />
            )}
            {otherPatches.map((patch, i) => {
              const key =
                patch.kind === "phase_change"
                  ? `phase-${patch.to}-${i}`
                  : `update-${patch.requirementId}-${patch.field}-${i}`;
              return <RequirementPatchCard key={key} patch={patch} />;
            })}
          </Box>
        )}

        {detectedSnapshot && (
          <IterationCard
            detection={detectedSnapshot}
            compact={compactIteration}
          />
        )}

        {!hideToolCallsPanel &&
          message.tool_calls &&
          message.tool_calls.length > 0 && (
            <ToolCallsPanel calls={message.tool_calls} />
          )}

        {quickReplyOptions.length > 0 && onSendFollowUp && (
          <QuickReplyChips
            options={quickReplyOptions}
            interactive={isLatestAssistant}
            isStreaming={isStreaming}
            onPick={(opt) => onSendFollowUp(opt)}
          />
        )}

        {/* Fallback export — only when BomTable did NOT render (i.e. the
            heuristic flagged the message as a BOM but no markdown table
            could be parsed). The new BomTable renders its own export
            button in its footer. */}
        {message.is_bom && !bomTable && (
          <Box sx={{ mt: 1.25 }}>
            <BomExportButton content={message.content} />
          </Box>
        )}

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            mt: 0.75,
          }}
        >
          <Box
            className="message-actions"
            sx={{
              opacity: 0,
              transition: (t) => `opacity ${t.motion.fast}`,
            }}
          >
            <CopyMessageButton text={message.content} />
          </Box>
          {message.timestamp && (
            <Typography
              variant="caption"
              sx={{
                color: "text.disabled",
                fontSize: "0.68rem",
              }}
            >
              {formatTime(message.timestamp)}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
}
